import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PDF } from 'shared/PitstopUI/pdf';
import { Button } from 'antd';
import mediaErrorTracker from '../../services/mediaErrorTracking';
import DocumentViewer from './DocumentViewer';

interface MediaPreviewProps {
  media?: string | File;
  isTiff?: boolean;
  tiffUrls?: string[];
  currentTiffIndex?: number;
  onNavigatePage?: (direction: 'prev' | 'next') => void;
  isFixed?: boolean;
  fixedWidth?: number;
  fixedLeft?: number;
  workOrderId?: string;
  loading?: boolean;
  isSlideOutMode?: boolean;
}

/**
 * @param props
 * @param props.loading - Whether the media is loading
 * @param props.media - The URL or base64 media to preview
 * @param props.isTiff - Whether the media is a TIFF image
 * @param props.tiffUrls - Array of URLs for TIFF pages
 * @param props.currentTiffIndex - Current index of the TIFF page being viewed
 * @param props.onNavigatePage - Callback function to navigate between TIFF pages
 * @param props.isFixed - Whether the preview should be in fixed position
 * @param props.fixedWidth - The width to use when in fixed position
 * @param props.fixedLeft - The left position to use when in fixed position
 */
const MediaPreview: React.FC<MediaPreviewProps> = ({
  media,
  isFixed,
  fixedWidth,
  fixedLeft,
  workOrderId,
  loading,
  isSlideOutMode,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const loadTrackingStarted = useRef(false);

  // Determine media type
  const getMediaType = useCallback((media: string | File): string => {
    // Handle File objects
    if (media instanceof File) {
      if (media.type === 'application/pdf') return 'pdf';
      if (media.type.includes('image/')) return 'image';
      return 'unknown';
    }

    // Handle string URLs
    if (typeof media === 'string') {
      if (media.includes('application/pdf') || media.includes('.pdf'))
        return 'pdf';
      if (media.includes('.tiff') || media.includes('.tif')) return 'tiff';
      if (media.includes('.png')) return 'png';
      if (media.includes('.jpg') || media.includes('.jpeg')) return 'jpeg';
      if (media.includes('.gif')) return 'gif';
    }

    return 'unknown';
  }, []);

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);

    if (media) {
      const mediaUrl = media instanceof File ? media.name : String(media);
      mediaErrorTracker.trackLoadSuccess(
        mediaUrl,
        workOrderId,
        getMediaType(media)
      );
    }
  }, [media, workOrderId, getMediaType]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);

    if (media) {
      const mediaUrl = media instanceof File ? media.name : String(media);
      mediaErrorTracker.trackMediaError({
        workOrderId,
        mediaUrl: mediaUrl,
        mediaType: getMediaType(media),
        errorType: 'rendering',
        errorMessage: 'Image failed to render in browser',
      });
    }
  }, [media, workOrderId, getMediaType]);

  // Track load start when media changes
  useEffect(() => {
    if (media && !loadTrackingStarted.current) {
      const mediaUrl = media instanceof File ? media.name : String(media);
      mediaErrorTracker.trackLoadStart(
        mediaUrl,
        workOrderId,
        getMediaType(media)
      );
      loadTrackingStarted.current = true;
      setImageError(false);
      setImageLoaded(false);
    }

    return () => {
      loadTrackingStarted.current = false;
    };
  }, [media, workOrderId, getMediaType]);

  // Track stuck spinner if loading too long
  useEffect(() => {
    if (loading && media) {
      const timer = setTimeout(() => {
        if (loading && !imageLoaded && !imageError) {
          const mediaUrl = media instanceof File ? media.name : String(media);
          mediaErrorTracker.trackStuckSpinner(
            workOrderId || 'unknown',
            mediaUrl,
            15
          );
        }
      }, 15000); // 15 second timeout for stuck spinner

      return () => clearTimeout(timer);
    }
  }, [loading, media, imageLoaded, imageError, workOrderId]);
  if (!media) {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    position: 'sticky',
    top: isSlideOutMode ? '0px' : '30px',
    // Remove transition to prevent animation during scroll
  };

  // Use DocumentViewer for all file types including PDF, images, and documents
  return (
    <div style={containerStyle}>
      <DocumentViewer
        media={media}
        isFixed={isFixed}
        fixedWidth={fixedWidth}
        fixedLeft={fixedLeft}
        skipContainerStyle={true}
      />
    </div>
  );
};

export default MediaPreview;
